import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { VehicleTypeRef } from '../models/vehicle-type.model';

export type VehicleStatus = 'frei' | 'einsatz' | 'wartung' | 'ausser' | 'geblockt';

/** What crews call a vehicle: its display name, else the plate. One helper so
 *  no screen invents its own label again. */
export function vehicleLabel(v?: { displayName?: string | null; licensePlate?: string | null } | null): string {
  return (v?.displayName || '').trim() || (v?.licensePlate || '').trim() || '—';
}

export interface ApiVehicle {
  id: string;
  licensePlate: string;
  /** Nickname crews use ("Mercedes 1"); the plate stands in when empty. */
  displayName?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  typeId?: string | null;
  /** The type row, embedded by the API. */
  type?: VehicleTypeRef | null;
  capacity: number;
  lyingPlaces: number;
  // Identification
  vin?: string | null;
  colorExt?: string | null;
  fuelType?: string | null;
  tankCapacity?: number | null;
  // Odometer & HU
  currentKm?: number | null;
  /** Write-only: the vehicle's driver-app login PIN. Responses carry
      `hasAuthPin` instead — the hash never leaves the backend. */
  authPin?: string;
  hasAuthPin?: boolean;
  nextHuDate?: string | null;
  // Purchase
  purchaseDate?: string | null;
  purchasePrice?: number | null;
  // Equipment
  isWheelchairAccessible: boolean;
  hasOxygenEquipment: boolean;
  hasStretcher: boolean;
  hasVacuumMat: boolean;
  hasSuction: boolean;
  hasMonitor: boolean;
  hasInfusion: boolean;
  // Assignment
  assignedDriverId?: string | null;
  driverName?: string | null;
  driverPhoto?: string | null;
  costsYtd?: number;
  // Status & notes
  status: VehicleStatus;
  isActive: boolean;
  notes?: string | null;
  maintenanceWindowStart?: string | null;
  maintenanceWindowEnd?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type VehicleCreateDto = Omit<ApiVehicle, 'id' | 'createdAt' | 'updatedAt'>;
export type VehicleUpdateDto = Partial<VehicleCreateDto>;

@Injectable({ providedIn: 'root' })
export class VehicleService {
  private readonly base = `${environment.apiUrl}/vehicles`;

  constructor(private http: HttpClient) {}

  private nocache(): { headers: HttpHeaders; params: { _t: string } } {
    return {
      headers: new HttpHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }),
      params: { _t: Date.now().toString() },
    };
  }

  list(): Observable<ApiVehicle[]> {
    return this.http.get<ApiVehicle[]>(this.base, this.nocache());
  }

  get(id: string): Observable<ApiVehicle> {
    return this.http.get<ApiVehicle>(`${this.base}/${id}`, this.nocache());
  }

  create(dto: VehicleCreateDto): Observable<ApiVehicle> {
    return this.http.post<ApiVehicle>(this.base, dto);
  }

  update(id: string, dto: VehicleUpdateDto): Observable<ApiVehicle> {
    return this.http.put<ApiVehicle>(`${this.base}/${id}`, dto);
  }

  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }
}
